import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';
import ImageCarousel from '../components/ImageCarousel';
import CategoryCarousel from '../components/CategoryCarousel';
import WhatsAppFloat from '../components/WhatsAppFloat';
import { getProdutos, getCategorias, getCarouselInicial } from '../services/api';
import './Home.css';

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const MAX_DESTAQUES = 30;

const Home = () => {
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselInicial, setCarouselInicial] = useState({ interval: 5000, slides: [] });

  const loadCarouselFromApi = useCallback(() => {
    getCarouselInicial()
      .then((res) => {
        const data = res.data || {};
        const interval = data.interval != null ? Math.max(2000, Math.min(60000, Number(data.interval))) : 5000;
        const slides = Array.isArray(data.slides) ? data.slides : [];
        setCarouselInicial({ interval, slides });
      })
      .catch(() => setCarouselInicial({ interval: 5000, slides: [] }));
  }, []);

  useEffect(() => {
    loadCarouselFromApi();
  }, [loadCarouselFromApi]);

  useEffect(() => {
    loadProdutos();
    loadCategorias();
  }, []);

  const loadProdutos = async () => {
    try {
      const response = await getProdutos();
      const data = response.data;
      const lista = Array.isArray(data) ? data : (data?.data || []);
      setProdutos(lista);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setProdutos([]);
      setLoading(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const response = await getCategorias();
      const data = response.data;
      setCategorias(Array.isArray(data) ? data : (data?.data || []));
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      setCategorias([]);
    }
  };

  const listaProdutos = Array.isArray(produtos) ? produtos : [];

  // Em destaque: até 30 produtos, ordem sorteada a cada carregamento da página
  const destaqueProdutos = useMemo(() => {
    const list = Array.isArray(produtos) ? produtos : [];
    if (list.length === 0) return [];
    return shuffleArray(list).slice(0, MAX_DESTAQUES);
  }, [produtos]);

  const produtosParaDestaque = destaqueProdutos.length > 0 ? destaqueProdutos : listaProdutos.slice(0, MAX_DESTAQUES);

  // Agrupar produtos por categoria
  const produtosPorCategoria = listaProdutos.reduce((acc, produto) => {
    const categoria = produto.categoria || 'Geral';
    if (!acc[categoria]) {
      acc[categoria] = [];
    }
    acc[categoria].push(produto);
    return acc;
  }, {});

  // Produtos em oferta (com preco_anterior) - até 30, sorteados a cada carregamento (mesma lógica dos destaques)
  const produtosOferta = useMemo(() => {
    const oferta = (Array.isArray(produtos) ? produtos : []).filter(
      p => p.preco_anterior && parseFloat(p.preco_anterior) > parseFloat(p.preco)
    );
    if (oferta.length === 0) return [];
    return oferta.length <= MAX_DESTAQUES ? shuffleArray(oferta) : shuffleArray(oferta).slice(0, MAX_DESTAQUES);
  }, [produtos]);

  // Categorias principais (com produtos)
  const categoriasPrincipais = [
    { nome: 'Maquiagem', icon: '💄', produtos: produtosPorCategoria['Maquiagem'] || [] },
    { nome: 'Cuidados com a Pele', icon: '✨', produtos: produtosPorCategoria['Cuidados com a Pele'] || [] },
    { nome: 'Cabelos', icon: '💇', produtos: produtosPorCategoria['Cabelos'] || [] },
    { nome: 'Perfumes', icon: '🌸', produtos: produtosPorCategoria['Perfumes'] || [] },
  ].filter(cat => cat.produtos.length > 0);

  return (
    <div className="home">
      <Helmet>
        <title>Loucas por Beleza | Produtos de beleza e maquiagem</title>
        <meta name="description" content="Loja online de produtos de beleza, maquiagem, cuidados com a pele e cabelos. Frete grátis acima de R$ 100." />
      </Helmet>
      {/* Carrossel de Imagens (controlado pelo admin em Carrossel inicial) */}
      <section className="carousel-section">
        <div className="container">
          <ImageCarousel
            images={carouselInicial.slides}
            autoPlay={true}
            interval={carouselInicial.interval}
            showControls={false}
          />
        </div>
      </section>

      <WhatsAppFloat />

      {/* Ofertas Especiais */}
      {produtosOferta.length > 0 && (
        <section className="ofertas-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                <span className="title-icon">🔥</span>
                Ofertas Especiais
              </h2>
              <Link to="/produtos" className="ver-todos-link">Ver todas</Link>
            </div>
            <div className="products-grid-mercadolivre produtos-oferta-grid">
              {produtosOferta.map(produto => (
                <ProductCard key={produto.id} produto={produto} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categorias */}
      {categoriasPrincipais.length > 0 && (
        <section className="categorias-section">
          <div className="container">
            <h2 className="section-title">
              <span className="title-icon">📦</span>
              Navegar por Categorias
            </h2>
            <div className="categorias-grid">
              {categoriasPrincipais.map((categoria, index) => (
                <CategoryCard 
                  key={index} 
                  categoria={categoria.nome}
                  icon={categoria.icon}
                  produtos={categoria.produtos.slice(0, 4)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Produtos em Destaque - até 30 produtos, sorteados a cada carregamento */}
      <section className="destaques-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              <span className="title-icon">⭐</span>
              Produtos em Destaque
            </h2>
            <Link to="/produtos" className="ver-todos-link">Ver todos</Link>
          </div>
          {loading ? (
            <div className="loading">Carregando produtos...</div>
          ) : produtosParaDestaque.length > 0 ? (
            <div className="products-grid-mercadolivre produtos-destaque-grid">
              {produtosParaDestaque.map(produto => (
                <ProductCard key={produto.id} produto={produto} />
              ))}
            </div>
          ) : (
            <div className="no-products">Nenhum produto encontrado</div>
          )}
        </div>
      </section>

      {/* Carrossel de Categorias */}
      {categorias.length > 0 && (
        <section className="category-carousel-section-wrapper">
          <div className="container">
            <CategoryCarousel categorias={categorias} />
          </div>
        </section>
      )}

      {/* Feature Blocks */}
      <section className="features">
        <div className="container">
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon blue">↻</div>
              <h3>30 Dias de Troca</h3>
              <p>Garantia de satisfação</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon yellow">🚚</div>
              <h3>Frete Grátis</h3>
              <p>Para compras acima de R$ 100</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon red">🔒</div>
              <h3>Pagamento Seguro</h3>
              <p>Seus dados protegidos</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon teal">🎁</div>
              <h3>Novos Produtos</h3>
              <p>Lançamentos toda semana</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

